'use client';

import { AnimationProps, motion } from 'motion/react';
import { AriaRole, ComponentPropsWithRef } from 'react';

import Wrap from '~/components/atoms/Wrap';
import type { AtLeastOne } from '~/lib/@types/helpers';
import { cn } from '~/lib/utils/classname';

export type WordPropsGenerator<T> = (args: {
  index: number;
  word: string;
}) => T;

export type LetterPropsGenerator<T> = (args: {
  index: number;
  wordIndex: number;
  letterIndex: number;
  letter: string;
  isSpace: boolean;
}) => T;

type InternalProps =
  | ({
      useMotion: true;
    } & AtLeastOne<{
      generateWordProps: WordPropsGenerator<AnimationProps>;
      generateLetterProps: LetterPropsGenerator<AnimationProps>;
    }>)
  | {
      useMotion?: false;
      generateWordProps?: WordPropsGenerator<
        React.HTMLAttributes<HTMLSpanElement>
      >;
      generateLetterProps?: LetterPropsGenerator<
        React.HTMLAttributes<HTMLSpanElement>
      >;
    };

export type Props = {
  text: string;
  role?: AriaRole;
  className?: string;
  wordWrapperClassName?: string;
} & ComponentPropsWithRef<'span'> &
  InternalProps;

const TextSplitter: React.FC<Props> = ({
  text,
  role,
  className,
  wordWrapperClassName,
  useMotion = false,
  generateWordProps,
  generateLetterProps,
  ref,
}) => {
  const Component = useMotion ? motion.span : 'span';

  return (
    <Component className={className} aria-label={text} role={role} ref={ref}>
      {text.split(/\s+/).map((word, wordIndex, words) => {
        const previousLength = words.reduce(
          (acc, word, currentIndex) =>
            currentIndex < wordIndex ? acc + word.length + 1 : acc,
          0
        );

        const wordProps = generateWordProps
          ? generateWordProps({
              index: wordIndex,
              word,
            })
          : {};

        return (
          <Wrap
            key={wordIndex}
            with={(children) => (
              <span
                data-splitter="word-wrapper"
                className={cn(
                  'pointer-events-none select-none',
                  wordWrapperClassName
                )}
              >
                <motion.span
                  data-splitter="word"
                  className="relative inline-block whitespace-nowrap"
                  aria-hidden
                  {...(wordProps as AnimationProps)}
                >
                  {children}
                </motion.span>
              </span>
            )}
            when={useMotion}
            fallback={(children) => (
              <span
                data-splitter="word"
                className="relative inline-block whitespace-nowrap"
                aria-hidden
                {...(wordProps as React.HTMLAttributes<HTMLSpanElement>)}
              >
                {children}
              </span>
            )}
          >
            {word.split('').map((letter, letterIndex) => {
              const letterProps = generateLetterProps
                ? generateLetterProps({
                    index: previousLength + letterIndex,
                    wordIndex,
                    letterIndex,
                    letter,
                    isSpace: false,
                  })
                : {};

              if (useMotion) {
                return (
                  <motion.span
                    className="relative inline-block"
                    key={[wordIndex, letterIndex].join('--')}
                    data-splitter="letter"
                    aria-hidden
                    {...(letterProps as AnimationProps)}
                  >
                    {letter}
                  </motion.span>
                );
              } else {
                return (
                  <span
                    className="relative inline-block"
                    key={[wordIndex, letterIndex].join('--')}
                    data-splitter="letter"
                    aria-hidden
                    {...(letterProps as React.HTMLAttributes<HTMLSpanElement>)}
                  >
                    {letter}
                  </span>
                );
              }
            })}

            {wordIndex < words.length - 1 && (
              <Component
                className="relative inline-block"
                data-splitter="space"
                aria-hidden
              >
                &nbsp;
              </Component>
            )}
          </Wrap>
        );
      })}
    </Component>
  );
};

export default TextSplitter;
